import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Globe2, CheckCircle2, Shield, Activity, 
  ArrowRight, Play, Award, Zap,
  TrendingUp, Users, Wallet, Trophy, Flame, Crown, Hexagon, Star, Mail
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import DemoVideoPlayer from '@/components/DemoVideoPlayer';
import SocialShareVideo from '@/components/SocialShareVideo';

const LandingPage = () => {
  const navigate = useNavigate();
  const [isDemoLoading, setIsDemoLoading] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  const handleGuestDemo = async () => {
    setIsDemoLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${baseUrl}/api/auth/guest`, { method: 'POST' });
      if (res.ok) {
        window.location.href = '/dashboard';
      }
    } catch (err) {
      console.error("Demo error", err);
    } finally {
      setIsDemoLoading(false);
    }
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const liveEvents = [
    { id: 1, text: "Aisha just received her circle's $1,200 pot", icon: Trophy, color: "text-amber-400" },
    { id: 2, text: "Marcus contributed $50 to his round", icon: TrendingUp, color: "text-lime-400" },
    { id: 3, text: "Sarah joined the 'NYC Savers' circle", icon: Users, color: "text-cyan-400" },
    { id: 4, text: "David reached a 4-week contribution streak!", icon: Flame, color: "text-orange-400" },
    { id: 5, text: "Elena upgraded to Gold Trust Tier", icon: Award, color: "text-yellow-400" },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-sans selection:bg-lime-500/30 overflow-x-hidden">
      
      {/* 1. Header Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-lime-500/20 flex items-center justify-center border border-lime-500/50">
              <Globe2 className="w-6 h-6 text-lime-400" />
            </div>
            <span className="text-xl font-black tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif' }}>Worldwide Susu</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-neutral-400">
            <a href="#how-it-works" className="hover:text-white transition-colors">How it works</a>
            <a href="#tiers" className="hover:text-white transition-colors">Trust Tiers</a>
            <a href="#achievements" className="hover:text-white transition-colors">Achievements</a>
          </nav>
          <div className="flex items-center gap-4">
            <Link to="/slides" data-testid="header-slides-link" className="hidden md:flex text-sm font-medium text-neutral-400 hover:text-white transition-colors">
              View Slides
            </Link>
            <button 
              onClick={handleLogin}
              data-testid="header-login-button"
              className="px-5 py-2.5 rounded-full bg-white text-black text-sm font-bold hover:bg-neutral-200 transition-colors"
            >
              Log In
            </button>
          </div>
        </div>
      </header>

      {/* 2. Live Activity Ticker */}
      <div className="pt-20 border-b border-white/5 bg-[#0A0A0A]">
        <div className="relative flex overflow-x-hidden border-y border-white/5 bg-white/[0.02]" data-testid="live-activity-ticker">
          <div className="py-3 animate-marquee whitespace-nowrap flex items-center">
            {[...liveEvents, ...liveEvents, ...liveEvents].map((event, idx) => {
              const Icon = event.icon;
              return (
                <div key={idx} className="flex items-center mx-8 text-sm font-medium text-neutral-300">
                  <Icon className={`w-4 h-4 mr-3 ${event.color}`} />
                  <span data-testid="live-activity-ticker-item">{event.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 3. Hero Section */}
      <section className="relative pt-24 pb-32 px-6 lg:pt-32 lg:pb-40 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/21ee3af5-6699-4a97-94d4-0b429cd1d1bc/images/8dcda657cb23bb121039caf93c75e8becc5cd4f3c7ff47576a55dcc9d8bda0f3.png"
            alt="Arena Background"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/40 via-[#0A0A0A]/80 to-[#0A0A0A]"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-lime-500/20 blur-[120px] rounded-full pointer-events-none"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8 backdrop-blur-md"
          >
            <span className="flex h-2 w-2 rounded-full bg-lime-500 animate-pulse"></span>
            <span className="text-xs font-bold uppercase tracking-widest text-lime-400">Live Global Rounds</span>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-5xl md:text-7xl font-black uppercase leading-[1.1] tracking-tight mb-6"
            style={{ fontFamily: 'Unbounded, sans-serif' }}
          >
            Save with your circle.<br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-lime-400 to-emerald-300">
              Take your turn
            </span> receiving the pot.
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mb-8 flex items-center justify-center gap-3 text-lg md:text-2xl font-black uppercase tracking-tight"
            style={{ fontFamily: 'Unbounded, sans-serif' }}
          >
            <Globe2 className="w-6 h-6 md:w-7 md:h-7 text-cyan-400 animate-spin-slow" />
            <span className="text-white">Meet &amp; create your club with </span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-lime-300 to-amber-300">anyone, globally.</span>
          </motion.div>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg md:text-xl text-neutral-400 max-w-2xl mx-auto mb-12 font-medium"
          >
            The arena for collective saving. Track your streak, level up your trust tier, and grow your money together in a high-energy rotation.
          </motion.p>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6"
          >
            <button
              onClick={handleGuestDemo}
              disabled={isDemoLoading}
              data-testid="hero-try-demo-button"
              className="w-full sm:w-auto px-8 py-4 rounded-full bg-lime-500 hover:bg-lime-400 text-black font-black text-lg transition-all transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
            >
              {isDemoLoading ? (
                <div className="w-6 h-6 border-2 border-black/20 border-t-black rounded-full animate-spin"></div>
              ) : (
                <>
                  <Play className="w-5 h-5 fill-current" />
                  Try the demo — no signup
                </>
              )}
            </button>
            <button
              onClick={handleLogin}
              data-testid="hero-login-button"
              className="w-full sm:w-auto px-8 py-4 rounded-full bg-white hover:bg-neutral-200 text-black font-black text-lg transition-all transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
            >
              <Mail className="w-5 h-5" />
              Log in or sign up
            </button>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-8 flex items-center justify-center gap-6 flex-wrap"
          >
            <button
              onClick={() => setVideoModalOpen(true)}
              data-testid="hero-watch-demo-video-button"
              className="group inline-flex items-center gap-3 text-sm font-bold text-neutral-300 hover:text-white transition-colors"
            >
              <span className="w-9 h-9 rounded-full bg-white/10 group-hover:bg-lime-500 group-hover:text-black flex items-center justify-center transition-all">
                <Play className="w-4 h-4 ml-0.5" fill="currentColor" />
              </span>
              Watch the 35-sec demo
            </button>
            <Link to="/slides" data-testid="hero-slides-link-secondary" className="text-sm font-bold text-neutral-500 hover:text-white flex items-center justify-center gap-2 group transition-colors">
              View as slides 
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* 3b. Demo Video Section (Sora 2 hybrid) */}
      <section id="demo-video" className="py-16 px-6 relative">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-lime-500/10 border border-lime-500/30 text-lime-400 text-xs font-bold uppercase tracking-wider mb-4">
              <Play className="w-3 h-3" fill="currentColor" />
              35-second AI walkthrough
            </div>
            <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              See Susu in action
            </h2>
            <p className="text-neutral-400 mt-3">Community, contribution, and the pot — explained in under a minute.</p>
          </div>
          <DemoVideoPlayer variant="card" />
        </div>
      </section>

      {/* 3b-social. Ready-to-share vertical social video */}
      <SocialShareVideo />

      {/* 3c. Global Circles — meet & create clubs with anyone worldwide */}
      <section id="global-circles" className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full bg-cyan-500/10 blur-[140px]"></div>
          <div className="absolute inset-0 opacity-[0.04] [background-image:radial-gradient(circle_at_center,rgba(255,255,255,0.5)_1px,transparent_1px)] [background-size:32px_32px]"></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-bold uppercase tracking-widest mb-6">
            <Globe2 className="w-3 h-3" />
            Global Circles
          </div>

          <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tight leading-[1.05] mb-6"
              style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Meet &amp; create your club with{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-lime-300 to-amber-300">
              anyone, anywhere
            </span>.
          </h2>

          <p className="text-neutral-400 max-w-2xl mx-auto text-lg mb-14">
            Family across three time zones. Friends from your old neighborhood.
            A group chat that decided to get serious. Your circle isn&apos;t limited
            to your zip code anymore.
          </p>

          {/* Rotating globe made of country/language rings */}
          <div className="relative mx-auto mb-14 w-72 h-72 md:w-96 md:h-96">
            {/* Central globe */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-500/20 to-lime-500/20 border border-white/10 backdrop-blur-md flex items-center justify-center">
              <Globe2 className="w-24 h-24 md:w-32 md:h-32 text-white/70 animate-spin-slow" strokeWidth={0.75} />
            </div>
            {/* Orbit ring 1 */}
            <div className="absolute inset-0 rounded-full border border-white/10 animate-spin-slow" style={{ animationDuration: '30s' }}>
              {[
                { code: '🇺🇸', name: 'USA' }, { code: '🇬🇭', name: 'Ghana' },
                { code: '🇬🇧', name: 'UK' },  { code: '🇮🇳', name: 'India' },
                { code: '🇨🇦', name: 'Canada' }, { code: '🇳🇬', name: 'Nigeria' },
                { code: '🇧🇷', name: 'Brazil' }, { code: '🇰🇪', name: 'Kenya' },
              ].map((c, i, arr) => {
                const angle = (i / arr.length) * 360;
                return (
                  <div
                    key={c.name}
                    className="absolute top-1/2 left-1/2 w-10 h-10 -ml-5 -mt-5"
                    style={{ transform: `rotate(${angle}deg) translateY(-${144 + 24}px) rotate(-${angle}deg)` }}
                  >
                    <div className="w-10 h-10 rounded-full bg-black/70 border border-white/15 flex items-center justify-center text-xl backdrop-blur-md hover:scale-110 hover:border-lime-400 transition-all cursor-default" title={c.name}>
                      {c.code}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Three benefits row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {[
              { Icon: Users, tint: 'text-cyan-400',  title: 'Any country, any timezone', body: 'Members can live 12 hours apart — automatic scheduling handles the rest.' },
              { Icon: Wallet, tint: 'text-lime-400',  title: 'Multi-currency circles', body: 'USD · CAD · GBP · NGN · GHS · INR · KES — contribute in the currency you earn in.' },
              { Icon: Shield, tint: 'text-amber-300', title: 'Trust travels with you', body: 'Every member is Plaid-verified where available, and every rotation strengthens your Trust Score.' },
            ].map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 text-left hover:border-cyan-500/40 transition-colors"
              >
                <b.Icon className={`w-6 h-6 ${b.tint} mb-3`} strokeWidth={1.5} />
                <div className="font-black text-white mb-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>{b.title}</div>
                <div className="text-sm text-neutral-400">{b.body}</div>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={handleLogin}
              data-testid="global-cta-create-club"
              className="px-6 py-3 rounded-full bg-lime-500 hover:bg-lime-400 text-black font-black text-sm transition-all transform hover:scale-105 flex items-center gap-2"
            >
              Create your global circle
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={handleGuestDemo}
              data-testid="global-cta-explore-demo"
              className="px-6 py-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold text-sm transition-colors"
            >
              Explore in demo mode
            </button>
          </div>
        </div>
      </section>

      {/* 4. Trust-and-Safety Strip */}
      <section className="border-y border-white/10 bg-[#0A0A0A]/80 py-8 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 text-center divide-x-0 md:divide-x divide-white/10">
            <div className="flex flex-col items-center gap-3">
              <Shield className="w-6 h-6 text-cyan-400" />
              <span className="text-sm font-bold text-neutral-300">Bank-grade encryption</span>
            </div>
            <div className="flex flex-col items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-lime-400" />
              <span className="text-sm font-bold text-neutral-300">Plaid-verified members</span>
            </div>
            <div className="flex flex-col items-center gap-3">
              <Wallet className="w-6 h-6 text-orange-400" />
              <span className="text-sm font-bold text-neutral-300">Stripe / PayPal payouts</span>
            </div>
            <div className="flex flex-col items-center gap-3">
              <Globe2 className="w-6 h-6 text-purple-400" />
              <span className="text-sm font-bold text-neutral-300">4,000-year tradition</span>
            </div>
          </div>
        </div>
      </section>

      {/* 5. How a round works (4-Step Explainer) */}
      <section id="how-it-works" className="py-24 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              How a Round Works
            </h2>
            <p className="text-neutral-400 text-lg">The cycle of trust, simplified into four steps.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 relative">
            <div className="hidden md:block absolute top-12 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-lime-500/0 via-lime-500/50 to-lime-500/0 z-0"></div>

            {[
              { num: "01", title: "Join", desc: "Enter a circle and lock in your position in the rotation.", icon: Users, color: "text-blue-400", bg: "bg-blue-400/10", border: "border-blue-400/30" },
              { num: "02", title: "Contribute", desc: "Add your share to the pot alongside your circle members.", icon: Zap, color: "text-orange-400", bg: "bg-orange-400/10", border: "border-orange-400/30" },
              { num: "03", title: "Rotate", desc: "Track the turn as it shifts around the circle board.", icon: Activity, color: "text-cyan-400", bg: "bg-cyan-400/10", border: "border-cyan-400/30" },
              { num: "04", title: "Receive", desc: "Take your turn receiving the full, combined pot.", icon: Trophy, color: "text-lime-400", bg: "bg-lime-400/10", border: "border-lime-400/30" },
            ].map((step, idx) => (
              <div key={idx} className="relative z-10 p-8 rounded-3xl bg-[#111111] border border-white/5 hover:border-white/20 transition-colors group">
                <div className={`w-16 h-16 rounded-2xl ${step.bg} ${step.border} border flex items-center justify-center mb-6 transform group-hover:scale-110 transition-transform`}>
                  <step.icon className={`w-8 h-8 ${step.color}`} />
                </div>
                <div className="text-sm font-black text-neutral-500 mb-2 font-mono">{step.num}</div>
                <h3 className="text-2xl font-bold mb-3">{step.title}</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Dashboard Teaser: Streak & Tiers & Badges */}
      <section id="tiers" className="py-24 px-6 bg-[#0F0F0F] border-y border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            
            {/* Daily Streak / Challenge Card */}
            <div className="md:col-span-8 p-8 rounded-3xl bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-white/10 flex flex-col justify-between group overflow-hidden relative">
              <div className="absolute -right-20 -top-20 w-64 h-64 bg-orange-500/10 blur-[80px] rounded-full group-hover:bg-orange-500/20 transition-colors"></div>
              
              <div className="flex justify-between items-start mb-12 relative z-10">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-bold uppercase mb-4">
                    <Flame className="w-3 h-3" />
                    Daily Quest
                  </div>
                  <h3 className="text-3xl font-black uppercase tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    7-Day Contribution Streak
                  </h3>
                  <p className="text-neutral-400 mt-2">Maintain your cadence and earn +20 Trust pts.</p>
                </div>
                <div className="w-16 h-16 rounded-full bg-[#222222] border border-white/10 flex items-center justify-center shadow-[0_0_30px_rgba(249,115,22,0.2)]">
                  <span className="text-2xl font-black text-white">4<span className="text-neutral-600">/7</span></span>
                </div>
              </div>

              <div className="relative z-10">
                <div className="flex gap-2 mb-2">
                  {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                    <div key={day} className={`flex-1 h-3 rounded-full ${day <= 4 ? 'bg-orange-500 shadow-[0_0_10px_rgba(249,115,22,0.5)]' : 'bg-white/5'}`}></div>
                  ))}
                </div>
                <div className="flex justify-between text-xs font-bold text-neutral-500 uppercase">
                  <span>Day 1</span>
                  <span>Goal: Day 7</span>
                </div>
              </div>
            </div>

            {/* Member Badge Gallery Teaser */}
            <div id="achievements" className="md:col-span-4 p-8 rounded-3xl bg-[#141414] border border-white/10 flex flex-col justify-between relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-lime-500/5 blur-[50px]"></div>
              <div>
                <h3 className="text-xl font-bold mb-2 uppercase tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif' }}>Trophy Room</h3>
                <p className="text-sm text-neutral-400 mb-8">Unlock achievements through trusted behavior.</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Crown, label: "Founder", color: "text-yellow-400", bg: "bg-yellow-400/10" },
                  { icon: Shield, label: "Guardian", color: "text-cyan-400", bg: "bg-cyan-400/10" },
                  { icon: Star, label: "All-Star", color: "text-lime-400", bg: "bg-lime-400/10" },
                  { icon: Hexagon, label: "Perfect", color: "text-purple-400", bg: "bg-purple-400/10" },
                ].map((badge, idx) => (
                  <div key={idx} className="flex flex-col items-center justify-center p-4 rounded-2xl bg-[#1A1A1A] border border-white/5 hover:border-white/20 transition-all cursor-pointer hover:-translate-y-1">
                    <badge.icon className={`w-8 h-8 ${badge.color} mb-2`} />
                    <span className="text-xs font-bold text-neutral-300">{badge.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Trust Tier Showcase */}
            <div className="md:col-span-12 p-8 md:p-12 rounded-3xl bg-[#111111] border border-white/10">
              <div className="text-center mb-12">
                <h3 className="text-3xl font-black uppercase tracking-tight mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>Level Up Your Trust</h3>
                <p className="text-neutral-400">Higher tiers unlock larger circles and premium platform features.</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { tier: "Bronze", pts: "0-200 pts", color: "from-[#B45309]/20 to-transparent", border: "border-[#B45309]/30", text: "text-[#B45309]" },
                  { tier: "Silver", pts: "201-500 pts", color: "from-[#94A3B8]/20 to-transparent", border: "border-[#94A3B8]/30", text: "text-[#94A3B8]" },
                  { tier: "Gold", pts: "501-1000 pts", color: "from-[#EAB308]/20 to-transparent", border: "border-[#EAB308]/30", text: "text-[#EAB308]" },
                  { tier: "Platinum", pts: "1000+ pts", color: "from-[#22D3EE]/20 to-transparent", border: "border-[#22D3EE]/30", text: "text-[#22D3EE]" },
                ].map((t, i) => (
                  <div key={i} className={`p-6 rounded-2xl bg-gradient-to-b ${t.color} border ${t.border} flex flex-col items-center text-center relative overflow-hidden group hover:-translate-y-2 transition-all duration-300`}>
                    <Hexagon className={`w-12 h-12 ${t.text} mb-4 opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all`} />
                    <h4 className={`text-xl font-black uppercase tracking-tight ${t.text} mb-1`} style={{ fontFamily: 'Unbounded, sans-serif' }}>{t.tier}</h4>
                    <span className="text-xs font-bold text-neutral-400">{t.pts}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 7. Footer */}
      <footer className="py-12 border-t border-white/10 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <Globe2 className="w-6 h-6 text-neutral-500" />
            <span className="text-lg font-black tracking-tight text-neutral-400" style={{ fontFamily: 'Unbounded, sans-serif' }}>Worldwide Susu</span>
          </div>
          
          <div className="flex gap-6 text-sm font-medium text-neutral-500">
            <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
            <a href="#" className="hover:text-white transition-colors">Plaid Legal</a>
          </div>

          <div className="text-sm text-neutral-600">
            &copy; 2026 Worldwide Susu. All rights reserved.
          </div>
        </div>
      </footer>

      <style dangerouslySetInnerHTML={{__html: `
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 20s linear infinite;
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 40s linear infinite;
        }
      `}} />

      {/* Fullscreen demo video modal */}
      {videoModalOpen && (
        <DemoVideoPlayer variant="modal" onClose={() => setVideoModalOpen(false)} />
      )}
    </div>
  );
};

export default LandingPage;
